import { useFeatureFlagsStore } from './useFeatureFlagsStore';
import {
  type FeaturesDto,
  type OrganizationFeatureKey,
  DEFAULT_FLAGS,
} from '@types';

/**
 * Get all feature flags
 * @returns FeaturesDto object with all flags
 * @example
 * const flags = getFeatureFlags();
 * console.log(flags.hubspot); // true or false
 */
export const getFeatureFlags = (): FeaturesDto => {
  try {
    return useFeatureFlagsStore.getState().flags;
  } catch {
    return DEFAULT_FLAGS;
  }
};

/**
 * Get a specific feature flag value
 * @param key - The feature flag key (e.g., 'hubspot', 'salesforce', 'aiScripts')
 * @returns boolean - true if enabled, false otherwise
 * @example
 * if (getFeature('hubspot')) {
 *   // Show HubSpot integration
 * }
 */
export const getFeature = (key: OrganizationFeatureKey): boolean => {
  try {
    const flags = useFeatureFlagsStore.getState().flags;
    return flags[key] ?? DEFAULT_FLAGS[key] ?? false;
  } catch {
    return DEFAULT_FLAGS[key] ?? false;
  }
};

/**
 * Check if feature flags have been loaded
 * @returns boolean
 */
export const isFeatureFlagsLoaded = (): boolean => {
  try {
    return useFeatureFlagsStore.getState().isLoaded;
  } catch {
    return false;
  }
};

/**
 * Get the organization ID for which flags are loaded
 * @returns string | null
 */
export const getFeatureFlagsOrgId = (): string | null => {
  try {
    return useFeatureFlagsStore.getState().organizationId;
  } catch {
    return null;
  }
};

/**
 * Check if feature flags are currently loading
 * @returns boolean
 */
export const isFeatureFlagsLoading = (): boolean => {
  try {
    return useFeatureFlagsStore.getState().isLoading;
  } catch {
    return false;
  }
};

/**
 * Get multiple feature flags at once
 * @param keys - Array of feature flag keys
 * @returns Record of key -> boolean
 * @example
 * const { hubspot, salesforce } = getFeatures(['hubspot', 'salesforce']);
 */
export const getFeatures = (
  keys: OrganizationFeatureKey[],
): Partial<FeaturesDto> => {
  try {
    const flags = useFeatureFlagsStore.getState().flags;
    const result: Partial<FeaturesDto> = {};
    for (const key of keys) {
      result[key] = flags[key] ?? DEFAULT_FLAGS[key] ?? false;
    }
    return result;
  } catch {
    const result: Partial<FeaturesDto> = {};
    for (const key of keys) {
      result[key] = DEFAULT_FLAGS[key] ?? false;
    }
    return result;
  }
};

/**
 * Check if ANY of the given features are enabled
 * @param keys - Array of feature flag keys
 * @returns boolean - true if at least one is enabled
 * @example
 * if (hasAnyFeature(['hubspot', 'salesforce', 'outreach'])) {
 *   // Show CRM integrations section
 * }
 */
export const hasAnyFeature = (keys: OrganizationFeatureKey[]): boolean => {
  try {
    const flags = useFeatureFlagsStore.getState().flags;
    return keys.some((key) => flags[key]);
  } catch {
    return false;
  }
};

/**
 * Check if ALL of the given features are enabled
 * @param keys - Array of feature flag keys
 * @returns boolean - true only if all are enabled
 * @example
 * if (hasAllFeatures(['hubspot', 'hubspotCustom'])) {
 *   // Show advanced HubSpot features
 * }
 */
export const hasAllFeatures = (keys: OrganizationFeatureKey[]): boolean => {
  try {
    const flags = useFeatureFlagsStore.getState().flags;
    return keys.every((key) => flags[key]);
  } catch {
    return false;
  }
};
